import React, { useContext, useState } from 'react'
import AppContext from '../../../../context/AppContext'
import { DiscountsCartData, ProviderCartData } from '../../../../interfaces/carts'
import { applyCouponToCart, removeCouponFromCart } from '../../../../clients/coupon'
import { getCartData } from '../../functions'
import { formatCurrency } from '../../../../utils/intl'
import './index.scss'

interface Props {
  provider: ProviderCartData
}

const isV2 = (d: DiscountsCartData) => d.source === 'coupon-v2' || !!d.cupon_id

/**
 * Cupón marketplace v2 en el carro: el usuario ingresa un código y se valida
 * contra orders/coupons. Reemplaza el flujo legacy de toggles por proveedor.
 * Está acotado al proveedor (un proveedor = una orden = un cupón).
 */
function CouponV2({ provider }: Props) {
  const { cartData, handlerCartData } = useContext(AppContext)
  const [codigo, setCodigo] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const applied = cartData.discounts.find(
    (d) => d.providerId === provider._id && isV2(d) && d.aplica !== false,
  )

  const refresh = async (): Promise<void> => handlerCartData(await getCartData())

  const apply = async (): Promise<void> => {
    const code = codigo.trim().toUpperCase()
    if (!code) return
    setLoading(true)
    setError('')
    try {
      const res: any = await applyCouponToCart(cartData._id, code)
      // El backend puede responder 2xx con { ok:false, motivo, mensaje } cuando el
      // cupón NO aplica (fuera de comuna, bajo mínimo, ya hay otro). No damos por
      // aplicado sin verificar: mostramos el motivo real, sin "✓" falso.
      if (res && (res.ok === false || res.motivo || res.aplica === false)) {
        setError(res.mensaje || res.motivo || 'No se pudo aplicar el cupón')
        return
      }
      await refresh()
      setCodigo('')
    } catch (e: any) {
      const data = e?.response?.data
      setError(
        (data && (data.mensaje || data.motivo)) ||
          (typeof data === 'string' && data) ||
          (e && e.message) ||
          'No se pudo aplicar el cupón',
      )
    } finally {
      setLoading(false)
    }
  }

  const remove = async (): Promise<void> => {
    if (!applied || !applied.cupon_id) return
    setLoading(true)
    setError('')
    try {
      await removeCouponFromCart(cartData._id, applied.cupon_id)
      await refresh()
    } catch (e: any) {
      const data = e?.response?.data
      setError(
        (data && (data.mensaje || data.motivo)) ||
          (typeof data === 'string' && data) ||
          (e && e.message) ||
          'No se pudo remover el cupón',
      )
    } finally {
      setLoading(false)
    }
  }

  if (applied) {
    return (
      <div className="coupon-v2 coupon-v2--applied">
        <div className="coupon-v2__info">
          <h6>¡Descuento aplicado!</h6>
          <span>
            {applied.codigo}: -{formatCurrency(applied.descuento_real || applied.amount || 0)}
          </span>
        </div>
        {/* En cupones auto-aplicables no mostramos "Quitar": se re-aplica solo
            al recargar el carro, así que quitarlo no tiene efecto. */}
        {(applied as any).requiere_codigo !== false && (
          <button className="coupon-v2__remove" onClick={remove} disabled={loading}>
            Quitar
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="coupon-v2">
      <div className="coupon-v2__input">
        <input
          type="text"
          placeholder="Ingresar código"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          disabled={loading}
        />
        <button onClick={apply} disabled={loading || !codigo.trim()}>
          {loading ? '...' : 'Aplicar'}
        </button>
      </div>
      {error && <p className="coupon-v2__error">{error}</p>}
    </div>
  )
}

export default CouponV2
